import { type RefObject, useEffect, useState } from 'react'

interface CursorPosition {
	x: number
	y: number
}

export function useCustomCursor(ref: RefObject<HTMLElement | null>) {
	const [position, setPosition] = useState<CursorPosition>({ x: 0, y: 0 })
	const [isVisible, setIsVisible] = useState(false)
	const [mouseGlobal, setMouseGlobal] = useState<CursorPosition>({ x: 0, y: 0 })

	useEffect(() => {
		const updateCursor = (x: number, y: number) => {
			if (!ref.current) {
				return
			}

			const rect = ref.current.getBoundingClientRect()

			const isInside =
				x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom

			setIsVisible(isInside)

			if (isInside) {
				setPosition({
					x: x - rect.left,
					y: y - rect.top,
				})
			}
		}

		const handleMouseMove = (e: MouseEvent) => {
			const { clientX, clientY } = e
			setMouseGlobal({ x: clientX, y: clientY })

			const elementUnderCursor = document.elementFromPoint(clientX, clientY)

			if (
				!ref.current ||
				!elementUnderCursor ||
				!ref.current.contains(elementUnderCursor)
			) {
				setIsVisible(false)
				return
			}

			const rect = ref.current.getBoundingClientRect()
			setPosition({
				x: clientX - rect.left,
				y: clientY - rect.top,
			})
			setIsVisible(true)
		}

		const handleScroll = () => {
			updateCursor(mouseGlobal.x, mouseGlobal.y)
		}

		const handleMouseLeave = () => {
			setIsVisible(false)
		}

		const handleVisibilityChange = () => {
			if (document.hidden) {
				setIsVisible(false)
			}
		}

		document.addEventListener('mousemove', handleMouseMove)
		document.addEventListener('mouseleave', handleMouseLeave)
		document.addEventListener('visibilitychange', handleVisibilityChange)
		window.addEventListener('scroll', handleScroll, { passive: true })

		return () => {
			document.removeEventListener('mousemove', handleMouseMove)
			document.removeEventListener('mouseleave', handleMouseLeave)
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			window.removeEventListener('scroll', handleScroll)
		}
	}, [ref, mouseGlobal.x, mouseGlobal.y])

	return { position, isVisible }
}
